/**
 * Claims del token que el `/me` NO expone hoy pero el BORDE sí usa para decidir:
 * `is_clinician` (clinical wall), `modules` y `suspended`. Los lee
 * del propio token del usuario — no lo valida, sólo lo lee.
 *
 * ⚠️ Es un puente temporal: lo correcto es que `/api/v1/auth/me` los devuelva
 * dentro de `user` (issue al BFF). Si no se pueden leer, devuelve `undefined` y
 * el resolvedor degrada sin restringir (ver `authz.ts`).
 */
function readTokenAuthzClaims(token?: string | null): {
  modules?: string[];
  isClinician?: boolean;
  suspended?: boolean;
} {
  try {
    const raw =
      token !== undefined
        ? token
        : typeof localStorage !== "undefined"
        ? localStorage.getItem("token")
        : null;
    const payload = raw?.split(".")[1];
    if (!payload) return {};
    const claims = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    );
    return {
      suspended: claims?.suspended === true,
      modules: Array.isArray(claims?.modules) ? claims.modules : undefined,
      isClinician:
        typeof claims?.is_clinician === "boolean"
          ? claims.is_clinician
          : undefined,
    };
  } catch {
    return {};
  }
}

/**
 * Sesión de IMPERSONACIÓN (act-as): el token de la consola de plataforma trae el
 * claim `act` de RFC 8693 — el `sub` del token es el usuario impersonado y `act`
 * identifica al staff que está actuando en su nombre.
 *
 * Se usa para que la app MUESTRE que no es una sesión normal (trd-client#92): un
 * modo act-as invisible es fuente de incidentes —quien mira la pantalla tiene que
 * saberlo, y quien audita después tiene que poder distinguir "lo hizo el cliente"
 * de "lo hizo un staff en su nombre"—. Lee el token, no lo valida (eso es del
 * borde). Si no se puede leer, devuelve `isActAs: false` y la app se comporta
 * como siempre.
 */
export function readActAsSession(): {
  isActAs: boolean;
  /** Quién actúa realmente (el staff), del claim `act`. */
  actor?: string;
  /** Mail del usuario impersonado (el sujeto efectivo del token). */
  impersonatedEmail?: string;
} {
  try {
    if (typeof localStorage === "undefined") return { isActAs: false };
    const raw = localStorage.getItem("token");
    const payload = raw?.split(".")[1];
    if (!payload) return { isActAs: false };
    const claims = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    );
    const act = claims?.act;
    if (!act) return { isActAs: false };
    return {
      isActAs: true,
      actor:
        (typeof act === "string" ? act : act?.sub || act?.email) || undefined,
      impersonatedEmail:
        typeof claims?.email === "string" ? claims.email : undefined,
    };
  } catch {
    return { isActAs: false };
  }
}

/**
 * Adapter del `/me`: lo aplana a la shape ÚNICA que consume el front (un `user`
 * plano con `modules`/`permissions`/`type`/`businessId`/plan/…), tolerando las DOS
 * formas del envelope durante la transición del BFF.
 *
 * El `/me` flag-ON se reshapeó (`trd-bff/src/modules/auth/domain/me_types.rs`,
 * trd-core#954): `modules`, `permissions` y `organization` (con `humanHealth`)
 * pasan a TOP-LEVEL una sola vez; `type` (owner/employee) se mudó a
 * `business.role` y `businessId`/`businessName` a `business.{id,name}`; y la data
 * operativa (plan status/limits, warehouses, industrias, avatar, MP…) a una
 * sección `operational`. `businessPlanFeatures` se ELIMINÓ a propósito — el
 * entitlement es `modules` (ver `hasBusinessFeature`/`resolveModuleAccess`).
 *
 * Este adapter re-aplana esas secciones sobre `user` SÓLO cuando vienen top-level;
 * si no (shape vieja con todo dentro de `user`), los guards `!== undefined` +
 * `body.business` lo dejan intacto. Backward Y forward compatible: hoy el `/me`
 * todavía duplica en `user`, así que preferimos top-level cuando existe y caemos a
 * `user.*` (via el spread base). Acepta el /me enveloped (`{data:…}`) o pelado.
 * Devuelve `undefined` si no hay `user` (no rompe el login).
 */
export function meUserWithOrg(me: any): any {
  const body = me?.data ?? me;
  const user = body?.user;
  if (!user) return user;
  return {
    ...user,
    // Data operativa (plan/warehouses/industrias/avatar/MP…) — camelCase 1:1 con
    // lo que el front leía de `user.*`. Ausente en la shape vieja => no-op.
    ...(body.operational ?? {}),
    ...(body.organization ? { organization: body.organization } : {}),
    ...(body.modules !== undefined ? { modules: body.modules } : {}),
    ...(body.permissions !== undefined
      ? { permissions: body.permissions }
      : {}),
    // `business` reemplaza `user.type`/`user.businessId`/`user.businessName`.
    ...(body.business
      ? {
          businessId: body.business.id,
          businessName: body.business.name,
          type: body.business.role,
        }
      : {}),
  };
}

export function normalizeIdentityUser(user: any, token?: string | null): any {
  if (!user) return user;

  // Sólo se puentean los claims del borde (`modules` = entitlement, `is_clinician`)
  // que el `/me` todavía no trae y que `can()` necesita. Ya NO se deriva `user.paths`:
  // el enforcement pasa por `can(resource)` leyendo `user.permissions` (vocab
  // canónico view/modify/delete), no por la shape vieja r/w/d con pérdida de
  // `paths.ts` (#49). El menú del usuario logueado también deriva de `permissions`.
  // ClientLayout pasa el token del PlatformStore: en desktop vive en Tauri,
  // no en localStorage. El fallback sólo sirve para los callers web síncronos.
  const { modules, isClinician, suspended } = readTokenAuthzClaims(token);
  return {
    ...user,
    // El claim usado por el BFF prevalece sobre el user persistido. Leerlo no
    // verifica la firma: esta es sólo UX; la autorización real sigue en el borde.
    ...(suspended !== undefined ? { suspended } : {}),
    ...(modules !== undefined && user.modules === undefined ? { modules } : {}),
    ...(isClinician !== undefined && user.isClinician === undefined
      ? { isClinician }
      : {}),
  };
}

export const handleErrorMessage = (error: any) => {
  // Si el error ya fue procesado por el interceptor de Axios, usar userMessage
  // Prioriza el mensaje real del backend (body de la respuesta) para no tragar
  // el detalle (ej. identity 422 "missing field organization_slug", 401).
  // El BFF envuelve en `{ statusCode, data: { message } }` (p. ej. el 404 del /me
  // "no está materializado en core", ADR-0009 F17): sin leer `data.data.message`
  // el motivo se perdía y quedaba "Hubo un problema, intente más tarde".
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.data?.message ||
    error?.response?.data?.error ||
    error?.userMessage ||
    error?.message ||
    "Hubo un problema, intente más tarde";
  const errorCode =
    error?.response?.data?.errorCode || error?.originalError?.errorCode;

  // Decidir en qué campo mostrar el mensaje
  switch (errorCode) {
    case "INVALID_EMAIL":
    case "BUYER_NOT_FOUND":
    case "EMPLOYEE_NOT_FOUND":
      return { email: message, password: "", generic: "" };

    case "INVALID_PASSWORD":
    case "INVALID_CREDENTIALS":
      return { email: "", password: message, generic: "" };

    default:
      return { email: "", password: "", generic: message };
  }
};
